/** Admin — customise which widgets each role dashboard can access (/admin/role-dashboards). */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, Eye, RotateCcw, Save } from "lucide-react";

export default function DashboardAdmin() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [active, setActive] = useState("management");
  const [draft, setDraft] = useState([]);
  const [target, setTarget] = useState("");
  const [saving, setSaving] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = async () => {
    const { data } = await api.get("/role-dashboards/admin/config");
    setCfg(data); setTarget(data.targets?.sales_target_mtd ?? "");
    const d = data.dashboards.find((x) => x.key === active); if (d) setDraft(d.widgets);
  };
  useEffect(() => { load().catch(() => setDenied(true)); }, []); // eslint-disable-line
  useEffect(() => { const d = cfg?.dashboards.find((x) => x.key === active); if (d) setDraft(d.widgets); }, [active, cfg]);

  if (denied) return <div className="py-24 text-center text-sm text-slate-500" data-testid="da-denied">Only Admin / Management can customise role dashboards.</div>;
  if (!cfg) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const dash = cfg.dashboards.find((x) => x.key === active);
  const toggle = (k) => setDraft((d) => d.includes(k) ? d.filter((x) => x !== k) : [...d, k]);
  const move = (k, dir) => setDraft((d) => { const i = d.indexOf(k); const j = i + dir; if (j < 0 || j >= d.length) return d; const n = [...d]; [n[i], n[j]] = [n[j], n[i]]; return n; });

  const save = async () => {
    setSaving(true);
    try { await api.put(`/role-dashboards/admin/config/${active}`, { widgets: draft }); toast.success(`${dash.label} dashboard saved`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  const reset = async () => {
    setSaving(true);
    try { await api.delete(`/role-dashboards/admin/config/${active}`); toast.success("Reset to defaults"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); } finally { setSaving(false); }
  };
  const saveTarget = async () => {
    try { await api.put("/role-dashboards/admin/targets", { sales_target_mtd: target === "" ? null : Number(target) }); toast.success("Sales target saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1300px] mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-5" data-testid="dashboard-admin">
      <div>
        <div className="font-mono text-[10px] font-semibold tracking-[0.18em] uppercase text-sky-600">Administration</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Role Dashboards</h1>
        <p className="text-sm text-slate-500 mt-0.5">Choose which widgets each role can access. Changes apply immediately to everyone in that role.</p>
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-4">
        <aside className="rounded-lg border border-slate-200 bg-white p-2 space-y-0.5" data-testid="da-dashboards">
          {cfg.dashboards.map((d) => (
            <button key={d.key} onClick={() => setActive(d.key)} data-testid={`da-dash-${d.key}`}
              className={`w-full text-left rounded-md px-3 py-2 text-[13px] ${active === d.key ? "bg-slate-900 text-white" : "hover:bg-slate-50 text-slate-700"}`}>
              <div className="flex items-center justify-between"><span>{d.label}</span><span className="font-mono text-[10px] opacity-70">{d.widgets.length}</span></div>
              <div className={`text-[10px] truncate ${active === d.key ? "text-slate-300" : "text-slate-400"}`}>{d.roles.join(", ") || "no roles mapped"}{d.customised ? " · customised" : ""}</div>
            </button>
          ))}
        </aside>

        <section className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">{dash.label} widgets</h2>
              <span className="text-[11px] text-slate-400">{draft.length} selected</span>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/dashboard?view=${active}`)} data-testid="da-preview"><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button>
                <Button variant="outline" size="sm" onClick={reset} disabled={saving} data-testid="da-reset"><RotateCcw className="h-3.5 w-3.5 mr-1" />Reset</Button>
                <Button size="sm" onClick={save} disabled={saving || draft.length === 0} data-testid="da-save"><Save className="h-3.5 w-3.5 mr-1" />Save</Button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
              {cfg.widgets.map((w) => {
                const on = draft.includes(w.key); const idx = draft.indexOf(w.key);
                return (
                  <label key={w.key} className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[13px] cursor-pointer ${on ? "border-sky-300 bg-sky-50/50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`da-widget-${w.key}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(w.key)} data-testid={`da-check-${w.key}`} />
                    <span className="flex-1 truncate">{w.label}</span>
                    {on && (
                      <span className="flex items-center gap-0.5 text-slate-400">
                        <span className="font-mono text-[10px] w-4 text-right">{idx + 1}</span>
                        <button type="button" onClick={(e) => { e.preventDefault(); move(w.key, -1); }} className="hover:text-slate-900 px-0.5">↑</button>
                        <button type="button" onClick={(e) => { e.preventDefault(); move(w.key, 1); }} className="hover:text-slate-900 px-0.5">↓</button>
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3" data-testid="da-targets">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Sales target (month-to-date, ₹)</div>
              <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. 25000000" data-testid="da-target-input"
                className="mt-1 h-9 w-56 rounded-md border border-slate-200 px-2 text-sm font-mono" />
            </div>
            <Button size="sm" onClick={saveTarget} data-testid="da-target-save">Save target</Button>
            <span className="text-[11px] text-slate-400">Drives the Sales Target widget on the Sales dashboard.</span>
          </div>
        </section>
      </div>
    </div>
  );
}
